<template>
  <div class="theme-form h-full overflow-y-auto px-4 pb-8 text-gray-dark">
    <div class="theme-sections min-h-full content-start">
      <div v-for="section in sections"
           :key="section.title"
           class="theme-section min-w-0"
      >
        <div class="text-gray-dark font-bold text-xl">{{ section.title }}</div>

        <div v-for="field in section.textFields"
             :key="field.key"
        >
          <base-label :id="field.key"
                      :label="field.label"
                      :tooltip="field.tooltip"
                      label-classes="font-normal"
                      class="mt-2"
          />
          <component :name="field.key"
                     :is="field.component"
                     :value="field.value"
                     :max-length="25"
                     class="mt-1"
                     @input="field.method($event)"
          />
        </div>

        <div v-for="field in section.uploadFields"
             :key="field.key"
        >
          <base-label :id="field.key"
                      :label="field.label"
                      :tooltip="field.tooltip"
                      label-classes="font-normal"
          />
          <component :name="field.key"
                     :is="field.component"
                     :value="field.value"
                     class="mt-1"
                     :lang-config="value.langConfig"
                     :theme-config="value.themeConfig"
                     :compact="field.compact"
                     @input="field.method($event)"
          />
        </div>

        <div v-if="section.toggleFields.length" class="mt-4">
          <div v-for="field in section.toggleFields"
               :key="field.key"
               class="flex items-center gap-2 mt-2"
          >
            <component :name="field.key"
                       :is="field.component"
                       :value="field.value"
                       @input="field.method($event)"
            />
            <div class="flex items-center">
              <label class="text-gray-dark font-normal mb-0 cursor-pointer whitespace-nowrap"
                     @click.prevent="toggleField(field)"
              >
                {{ field.label }}
              </label>
              <vs-tooltip v-if="field.tooltip" class="flex ml-1 items-center" :position="field.tooltipPosition || 'bottom'">
                <template #content>
                  <span v-html="field.tooltip"/>
                </template>
                <vs-icon icon="info" class="text-gray cursor-default"/>
              </vs-tooltip>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseLabel from '@/components/base-components/BaseLabel.vue'
import BaseSwitch from '@/components/base-components/BaseSwitch.vue'
import ValidatedInput from '@/components/base-components/ValidatedInput.vue'
import BaseFileInput from '@/components/base-components/BaseFileInput.vue'
import SettingsBotThemeLauncherIcon from '@/components/settings/bot-theme/SettingsBotThemeLauncherIcon.vue'
import omit from 'ramda/src/omit'

const UPLOAD_TOOLTIP = 'Add a PNG, JPG, or GIF that is under 150KB. Images with transparent backgrounds works best.'

function groupSection(section) {
  return {
    title: section.title,
    textFields: section.fields.filter(field => field.key === 'welcome' || field.key === 'buttonTitle'),
    uploadFields: section.fields.filter(field => field.key === 'headerLogo' || field.key === 'buttonIcon'),
    toggleFields: section.fields.filter(field => field.isToggle)
  }
}

export default {
  name: 'SettingsBotThemeForm',

  components: { BaseLabel },

  props: {
    value: {
      type: Object,
      default: () => {}
    },
    imageCache: {
      type: Object,
      required: true
    }
  },

  emits: ['input', 'update:image-cache'],

  data() {
    return {
      values: {
        widgetIcon: '',
        logoUrl: null
      }
    }
  },

  computed: {
    themeConfig: {
      get() {
        if (this.value.themeConfig) {
          return this.value.themeConfig
        }

        return {}
      },
      set(themeUpdate) {
        this.$emit('input', {
          themeConfig: { ...this.value.themeConfig, ...themeUpdate }
        })
      }
    },

    langConfig: {
      get() {
        if (this.value.langConfig) {
          return this.value.langConfig
        }

        return {}
      },
      set(langUpdate) {
        this.$emit('input', {
          langConfig: { ...this.value.langConfig, ...langUpdate }
        })
      }
    },

    themeHeaderLogoUrl() {
      return this.themeConfig.header && this.themeConfig.header.logoUrl
    },

    themeLauncherImageUrl() {
      return this.themeConfig.launcher && this.themeConfig.launcher.imageUrl
    },

    showWideViewButton() {
      return this.themeConfig.header?.showWideViewButton === true
    },

    showModalViewButton() {
      return this.themeConfig.header?.showModalViewButton === true
    },

    forceFullScreen() {
      return this.themeConfig.launcher?.forceFullScreen === true
    },

    logoOnly() {
      return this.themeConfig.header?.logoOnly === true
    },

    headerLogo: {
      get() {
        return this.imageCache.headerLogo || this.themeHeaderLogoUrl || null
      },
      set(headerLogo) {
        if (!headerLogo && this.themeHeaderLogoUrl) {
          this.themeConfig = {
            header: omit(['logoUrl'], this.value.themeConfig.header)
          }
          return
        }

        this.$emit('update:image-cache', {
          ...this.imageCache, headerLogo
        })
      }
    },

    buttonIcon: {
      get() {
        return this.imageCache.buttonIcon || this.themeLauncherImageUrl || null
      },
      set(buttonIcon) {
        if (!buttonIcon && this.themeLauncherImageUrl) {
          this.themeConfig = {
            launcher: omit(['imageUrl'], this.value.themeConfig.launcher)
          }
          return
        }

        this.$emit('update:image-cache', {
          ...this.imageCache, buttonIcon
        })
      }
    },

    sections() {
      return [
        {
          title: 'Chat header',
          fields: [
            {
              label: 'Text',
              key: 'welcome',
              method: headerText => this.setHeaderText(headerText),
              value: this.langConfig.default?.welcome || '',
              component: ValidatedInput
            },
            {
              label: 'Company logo',
              key: 'headerLogo',
              value: this.headerLogo,
              tooltip: UPLOAD_TOOLTIP,
              method: headerLogo => this.setHeaderLogo(headerLogo),
              component: BaseFileInput,
              compact: true
            },
            {
              label: 'Display logo only',
              key: 'logoOnly',
              value: this.logoOnly,
              tooltip: 'Default option is to display logo with text. Toggle on if you want to display logo only.',
              method: logoOnly => this.setLogoOnly(logoOnly),
              component: BaseSwitch,
              isToggle: true
            },
            {
              label: 'Show "Wide View" button',
              key: 'showWideViewButton',
              value: this.showWideViewButton,
              tooltip: 'Shows a button in the chat header that expands the widget into a wider layout.',
              method: showWideViewButton => this.setShowWideViewButton(showWideViewButton),
              component: BaseSwitch,
              isToggle: true
            },
            {
              label: 'Show "Modal View" button',
              key: 'showModalViewButton',
              value: this.showModalViewButton,
              tooltip: 'Shows a button in the chat header that opens the chat into a wider layout and centered in the screen in modal view.',
              method: showModalViewButton => this.setShowModalViewButton(showModalViewButton),
              component: BaseSwitch,
              isToggle: true
            }
          ]
        },
        {
          title: 'Launcher',
          fields: [
            {
              label: 'Text',
              key: 'buttonTitle',
              method: buttonTitle => this.setButtonTitle(buttonTitle),
              value: this.langConfig.default?.['buttonTitle'] || '',
              component: ValidatedInput
            },
            {
              label: 'Widget icon',
              key: 'buttonIcon',
              value: this.buttonIcon,
              tooltip: UPLOAD_TOOLTIP,
              method: buttonIcon => this.setButtonIcon(buttonIcon),
              component: SettingsBotThemeLauncherIcon
            },
            {
              label: 'Chat opens in full screen',
              key: 'forceFullScreen',
              value: this.forceFullScreen,
              tooltip: 'When the chat is opened, it will open in full screen and cover the entire screen.',
              tooltipPosition: 'top',
              method: forceFullScreen => this.setForceFullScreen(forceFullScreen),
              component: BaseSwitch,
              isToggle: true
            }
          ]
        }
      ].map(groupSection)
    }
  },

  methods: {
    setHeaderText(headerText) {
      if (!this.value.themeConfig) return
      this.langConfig = {
        default: {
          ...this.langConfig.default,
          welcome: headerText
        }
      }
    },

    setLogoOnly(logoOnly) {
      if (!this.value.themeConfig) return

      this.themeConfig = {
        header: {
          ...this.value.themeConfig.header,
          logoOnly
        }
      }
    },

    setButtonTitle(buttonTitle) {
      if (!this.value.themeConfig) return
      this.langConfig = {
        default: {
          ...this.langConfig.default,
          buttonTitle
        }
      }
    },

    setHeaderLogo(file) {
      this.headerLogo = file
    },

    setButtonIcon(file) {
      this.buttonIcon = file
    },

    toggleField(field) {
      field.method(!field.value)
    },

    setShowWideViewButton(showWideViewButton) {
      if (!this.value.themeConfig) return

      if (showWideViewButton) {
        this.themeConfig = {
          header: {
            ...this.value.themeConfig.header,
            showWideViewButton: true
          }
        }
        return
      }

      this.themeConfig = {
        header: omit(['showWideViewButton'], this.value.themeConfig.header)
      }
    },

    setShowModalViewButton(showModalViewButton) {
      if (!this.value.themeConfig) return

      if (showModalViewButton) {
        this.themeConfig = {
          header: {
            ...this.value.themeConfig.header,
            showModalViewButton: true
          }
        }
        return
      }

      this.themeConfig = {
        header: omit(['showModalViewButton'], this.value.themeConfig.header)
      }
    },

    setForceFullScreen(forceFullScreen) {
      if (!this.value.themeConfig) return

      if (forceFullScreen) {
        this.themeConfig = {
          launcher: {
            ...this.value.themeConfig.launcher,
            forceFullScreen: true
          }
        }
        return
      }

      this.themeConfig = {
        launcher: omit(['forceFullScreen'], this.value.themeConfig.launcher)
      }
    }
  }
}
</script>

<style scoped lang="scss">
.theme-form {
  container-type: inline-size;
}

.theme-sections {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
}

.theme-section + .theme-section {
  @apply border-0 border-t border-solid border-soft mt-8 pt-8;
}

// Vue scoped CSS does not rewrite selectors inside @container, so these
// selectors are written to outrank the single-column rules above.
@container (min-width: 600px) {
  .theme-form .theme-sections {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }

  .theme-form .theme-section:first-child {
    @apply pr-10;
  }

  .theme-form .theme-section + .theme-section {
    @apply border-t-0 border-l mt-0 pt-0 pl-10;
  }
}
</style>
